import {
  ActionMode,
  ExperienceFe,
  TabType,
  ViewMode,
} from "@intelligems/ig-types/fe-only/experiences";
import {
  AnalyticsViewType,
  DeviceType,
} from "@intelligems/ig-types/schemas/analytics/base";
import {
  BaseIntegrationDto,
  IntegrationName,
} from "@intelligems/ig-types/dtos/integrations";
import { ExperienceAnalysisDto } from "@intelligems/ig-types/dtos/experience-analysis";
import {
  ExperienceDto,
  ExperienceTestTypeDto,
  ExperienceType,
  ShippingCarrierProvider,
} from "@intelligems/ig-types/dtos/experiences";
import { ExperienceIntegrationDto } from "@intelligems/ig-types/dtos/experience-integrations";
import {
  isLegacyShippingSelection,
  ShippingTestMethodDefinitionDto,
} from "@intelligems/ig-types/dtos/shipping-test-method-definitions";
import {
  StayAiExperienceSettingsType,
  StayAiGlobalSettingsType,
} from "@intelligems/ig-types/schemas/stay-ai";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getExperienceFeFromDto } from "@src/api/formatters/from-dto/experiences";
import { initialExperienceFeDto } from "@src/api/initials";

import { ExperienceDetails, IExperienceState } from "./types";
import { isSameShippingSelection } from "./shippingSelectionIdentity";

export const initialState: IExperienceState = {
  isSuccess: false,
  experience: undefined,
  storedExperience: undefined,
  hasClickedSave: false,
  pageContentRef: null,
};

const experienceSlice = createSlice({
  name: "experience",
  initialState,
  reducers: {
    initExperimentFromDto: (
      draft,
      action: PayloadAction<{
        experience?: ExperienceDto;
        integrations?: BaseIntegrationDto[];
        testFlowType: ExperienceType;
        defaultCurrency?: string;
        name?: string;
        description?: string;
        tab?: TabType;
        isUpsell?: boolean;
      }>
    ) => {
      draft.isSuccess = true;
      draft.shippingSaveId = undefined;
      const {
        testFlowType,
        experience,
        integrations,
        defaultCurrency,
        name,
        description,
        isUpsell,
      } = action.payload;
      if (experience) {
        draft.experience = getExperienceFeFromDto(experience);
        draft.experience.actionMode = "edit";
        if (action.payload.tab) {
          draft.experience.tab = action.payload.tab;
        }
      } else {
        draft.experience = initialExperienceFeDto({
          defaultCurrency,
          tab: action.payload.tab,
          hasTestPricing: testFlowType === "pricing",
          hasTestShipping: testFlowType === "shipping",
          hasTestCampaign: testFlowType === "personalization/offer",
          hasTestCheckoutBlocks: testFlowType === "checkoutBlock",
          hasTestPostPurchase: testFlowType === "postPurchase",
          hasTestOnsiteUpsell: testFlowType === "onsiteUpsell",
          hasTestContent: true,
          testFlowType: action.payload.testFlowType,
          useWideApp: false,
          hasTestContentAdvanced:
            action.payload.testFlowType === "content/advanced",
          hasTestContentUrl: action.payload.testFlowType === "content/url",
          hasTestContentOnsite:
            action.payload.testFlowType === "content/onsiteEdits",
          hasTestContentTemplate:
            action.payload.testFlowType === "content/template",
          hasTestContentTheme: action.payload.testFlowType === "content/theme",
          hasTestOnsiteInjections: false,
          category: "experiment",
          name,
          description,
          integrations,
          isUpsell,
        });
      }
      draft.storedExperience = draft.experience;
    },
    initPersonalizationFromDto: (
      draft,
      action: PayloadAction<{
        experience: ExperienceDto | undefined;
        isUpsell?: boolean;
      }>
    ) => {
      const { experience, isUpsell } = action.payload;
      draft.isSuccess = true;
      if (experience) {
        draft.experience = getExperienceFeFromDto(experience);
        draft.experience.actionMode = "edit";
        draft.experience.viewMode = experience.archivedAtTs ? "view" : "edit";
      } else {
        draft.experience = initialExperienceFeDto({
          testFlowType: "personalization",
          useWideApp: false,
          defaultCurrency: undefined,
          hasTestPricing: false,
          hasTestShipping: false,
          hasTestCampaign: false,
          hasTestContent: false,
          hasTestContentAdvanced: false,
          hasTestContentUrl: false,
          hasTestContentOnsite: false,
          hasTestContentTemplate: false,
          hasTestContentTheme: false,
          category: "personalization",
          hasTestOnsiteInjections: false,
          hasTestCheckoutBlocks: false,
          hasTestPostPurchase: false,
          hasTestOnsiteUpsell: false,
          isUpsell,
        });
        draft.experience.actionMode = "create";
      }
      draft.storedExperience = draft.experience;
    },
    resetExperience: () => initialState,
    recordCompletedSave: (
      draft,
      action: PayloadAction<{ experienceId: string; at: number }>
    ) => {
      draft.lastCompletedSave = action.payload;
    },
    setShippingSaveId: (draft, action: PayloadAction<string>) => {
      draft.shippingSaveId = action.payload;
    },
    setExperienceId: (draft, action: PayloadAction<string | undefined>) => {
      if (!draft.isSuccess || !draft.experience) {
        return;
      }

      draft.experience.id = action.payload;
    },
    setExperienceTabWithoutRedirect: (
      draft,
      action: PayloadAction<TabType>
    ) => {
      if (!draft.isSuccess || !draft.experience) {
        return;
      }

      draft.experience.tab = action.payload;
    },
    setPageRefObj: (draft, action: PayloadAction<any>) => {
      if (!draft.isSuccess || !draft.experience) {
        return;
      }

      draft.pageContentRef = action.payload;
    },
    setExperienceTab: (
      draft,
      action: PayloadAction<
        TabType | { tab: TabType; mode: "push" | "replace" }
      >
    ) => {
      if (!draft.isSuccess || !draft.experience) {
        return;
      }

      const { tab, mode } =
        typeof action.payload === "string"
          ? {
              tab: action.payload,
              mode: "push",
            }
          : action.payload;

      draft.experience.tab = tab;
      const url = new URL(window.location.href);
      url.searchParams.set("tab", tab);
      if (mode === "push") {
        window.history.pushState(null, "", url.toString());
      } else {
        window.history.replaceState(null, "", url.toString());
      }
    },
    initExperienceDetails: (
      draft,
      action: PayloadAction<ExperienceDetails>
    ) => {
      if (!draft.isSuccess || !draft.experience) {
        return;
      }

      if (action.payload.name !== undefined) {
        draft.experience.name = action.payload.name;
      }
      if (action.payload.description !== undefined) {
        draft.experience.description = action.payload.description;
      }
      draft.storedExperience = draft.experience;
    },
    setExperienceDetails: (draft, action: PayloadAction<ExperienceDetails>) => {
      if (!draft.isSuccess || !draft.experience) {
        return;
      }

      if (action.payload.name !== undefined) {
        draft.experience.name = action.payload.name;
      }
      if (action.payload.description !== undefined) {
        draft.experience.description = action.payload.description;
      }
    },
    setExperienceViewMode: (draft, action: PayloadAction<ViewMode>) => {
      if (!draft.isSuccess || !draft.experience) {
        return;
      }

      draft.experience.viewMode = action.payload;
    },
    setExperienceActionMode: (draft, action: PayloadAction<ActionMode>) => {
      if (!draft.isSuccess || !draft.experience) {
        return;
      }

      draft.experience.actionMode = action.payload;
    },
    setExperienceDevicePreview: (
      draft,
      action: PayloadAction<{
        device?: DeviceType;
        path?: string;
      }>
    ) => {
      if (!draft.isSuccess || !draft.experience) {
        return;
      }

      if (action.payload.device) {
        if (draft.experience?.devicePreview) {
          draft.experience.devicePreview.device = action.payload.device;
        } else {
          draft.experience.devicePreview = {
            device: action.payload.device,
            path: "/",
          };
        }
      }

      if (action.payload.path !== undefined) {
        if (draft.experience?.devicePreview) {
          draft.experience.devicePreview.path = action.payload.path
            ? action.payload.path.startsWith("/")
              ? action.payload.path
              : `/${action.payload.path}`
            : "/";
        } else {
          draft.experience.devicePreview = {
            device: "desktop",
            path: action.payload.path
              ? action.payload.path.startsWith("/")
                ? action.payload.path
                : `/${action.payload.path}`
              : "/",
          };
        }
      }
      if (draft.storedExperience) {
        draft.storedExperience.devicePreview = draft.experience.devicePreview;
      }
    },
    setExperienceCurrency: (
      draft,
      action: PayloadAction<{
        code: string;
        symbol: string;
      }>
    ) => {
      if (!draft.isSuccess || !draft.experience) {
        return;
      }

      draft.experience.currency = action.payload;
    },
    setExperienceShippingProfile: (
      draft,
      action: PayloadAction<{
        deliveryProfileId: string;
        deliveryLocationGroupId: string;
        deliveryZoneId: string;
        methodDefinitionId: string | null;
      }>
    ) => {
      if (!draft.isSuccess || !draft.experience) {
        return;
      }

      // The zone checkbox is selected when any definition references the zone
      // (a saved experience can have multiple definitions per zone — a base
      // entry plus one per configured method definition). Deselecting must
      // therefore remove every definition for the zone, not just the first.
      const isZoneSelected =
        draft.experience.shippingTestMethodDefinitions.some(
          (def) =>
            isLegacyShippingSelection(def) &&
            def.deliveryZoneId === action.payload.deliveryZoneId
        );

      if (isZoneSelected) {
        draft.experience.shippingTestMethodDefinitions =
          draft.experience.shippingTestMethodDefinitions.filter(
            (def) =>
              !isLegacyShippingSelection(def) ||
              def.deliveryZoneId !== action.payload.deliveryZoneId
          );
      } else {
        draft.experience.shippingTestMethodDefinitions.push({
          deliveryProfileId: action.payload.deliveryProfileId,
          deliveryLocationGroupId: action.payload.deliveryLocationGroupId,
          deliveryZoneId: action.payload.deliveryZoneId,
          methodDefinitionId: action.payload.methodDefinitionId,
          canonicalMethodId: undefined,
          deleteTargetKind: undefined,
          deleteTargetRateDefinitionId: undefined,
          deleteTargetFreeCondition: undefined,
        });
      }
    },
    setExperienceShippingTestMethodDefinition: (
      draft,
      action: PayloadAction<ShippingTestMethodDefinitionDto>
    ) => {
      if (!draft.isSuccess || !draft.experience) {
        return;
      }

      if (!isLegacyShippingSelection(action.payload)) {
        const index = draft.experience.shippingTestMethodDefinitions.findIndex(
          (definition) => isSameShippingSelection(definition, action.payload)
        );
        if (index === -1) {
          draft.experience.shippingTestMethodDefinitions.push(action.payload);
        } else {
          draft.experience.shippingTestMethodDefinitions.splice(index, 1);
        }
        return;
      }
      const payload = action.payload;

      const deliveryZoneIndex =
        draft.experience.shippingTestMethodDefinitions.findIndex(
          (def) =>
            isLegacyShippingSelection(def) &&
            def.deliveryZoneId === payload.deliveryZoneId &&
            !def.methodDefinitionId
        );

      const methodDefIndex =
        draft.experience.shippingTestMethodDefinitions.findIndex((def) =>
          isSameShippingSelection(def, payload)
        );
      if (methodDefIndex === -1) {
        // method def needs to be added
        if (deliveryZoneIndex === -1) {
          draft.experience.shippingTestMethodDefinitions.push({
            deliveryProfileId: payload.deliveryProfileId,
            deliveryLocationGroupId: payload.deliveryLocationGroupId,
            deliveryZoneId: payload.deliveryZoneId,
            methodDefinitionId: payload.methodDefinitionId,
            canonicalMethodId: payload.canonicalMethodId,
            deleteTargetKind: payload.deleteTargetKind,
            deleteTargetRateDefinitionId: payload.deleteTargetRateDefinitionId,
            deleteTargetFreeCondition: payload.deleteTargetFreeCondition,
          });
        } else {
          draft.experience.shippingTestMethodDefinitions[deliveryZoneIndex] = {
            ...payload,
            id: draft.experience.shippingTestMethodDefinitions[
              deliveryZoneIndex
            ].id,
          };
        }
      } else {
        // remove
        const current =
          draft.experience.shippingTestMethodDefinitions[methodDefIndex];
        if (!isLegacyShippingSelection(current)) {
          return;
        }
        draft.experience.shippingTestMethodDefinitions[methodDefIndex] = {
          ...current,
          methodDefinitionId: null,
          canonicalMethodId: undefined,
          deleteTargetKind: undefined,
          deleteTargetRateDefinitionId: undefined,
          deleteTargetFreeCondition: undefined,
        };
      }
    },
    setExperienceAnalyticsViewType: (
      draft,
      action: PayloadAction<AnalyticsViewType>
    ) => {
      if (!draft.isSuccess || !draft.experience) {
        return;
      }

      draft.experience.experienceAnalysis.analyticsViewType = action.payload;
    },
    toggleRequiresLink: (
      draft,
      action: PayloadAction<{ requiresLink: boolean }>
    ) => {
      if (!draft.isSuccess || !draft.experience) {
        return;
      }

      draft.experience.requiresLink = action.payload.requiresLink;
      return draft;
    },
    setLinkBaseUrl: (draft, action: PayloadAction<{ linkBaseUrl: string }>) => {
      if (!draft.isSuccess || !draft.experience) {
        return;
      }

      draft.experience.linkBaseUrl = action.payload.linkBaseUrl;
      return draft;
    },
    setExperienceShippingCarrierProvider: (
      draft,
      action: PayloadAction<ShippingCarrierProvider>
    ) => {
      if (!draft.isSuccess || !draft.experience) {
        return;
      }

      draft.experience.shippingCarrierProvider = action.payload;
    },
    deleteShippingExpMethodDefinitionById: (
      draft,
      action: PayloadAction<string>
    ) => {
      if (!draft.isSuccess || !draft.experience) {
        return;
      }

      const methodDefIndexRemove =
        draft.experience.shippingTestMethodDefinitions.findIndex(
          (def) =>
            isLegacyShippingSelection(def) &&
            def.methodDefinitionId === action.payload
        );
      const current =
        draft.experience.shippingTestMethodDefinitions[methodDefIndexRemove];
      if (!current || !isLegacyShippingSelection(current)) {
        return;
      }
      draft.experience.shippingTestMethodDefinitions[methodDefIndexRemove] = {
        ...current,
        methodDefinitionId: null,
        canonicalMethodId: undefined,
        deleteTargetKind: undefined,
        deleteTargetRateDefinitionId: undefined,
        deleteTargetFreeCondition: undefined,
      };
    },
    resetTestType: (
      draft,
      action: PayloadAction<{
        testType: keyof ExperienceTestTypeDto;
        value: boolean;
      }>
    ) => {
      if (!draft.isSuccess || !draft.experience) {
        return;
      }

      draft.experience.testTypes[action.payload.testType] =
        action.payload.value;
    },
    switchContentTestType: (
      draft,
      action: PayloadAction<{ testType: ExperienceType }>
    ) => {
      if (!draft.isSuccess || !draft.experience) {
        return;
      }

      const { testType } = action.payload;
      draft.experience.type = testType;
      draft.experience.testTypes = {
        ...draft.experience.testTypes,
        hasTestContent: true,
        hasTestContentUrl: testType === "content/url",
        hasTestContentTemplate: testType === "content/template",
        hasTestContentTheme: testType === "content/theme",
        hasTestContentAdvanced: testType === "content/advanced",
        hasTestContentOnsite: testType === "content/onsiteEdits",
      };
    },
    // Flip the experience between an A/B test (`category: "experiment"`) and a
    // personalization (`category: "personalization"`) — the axis orthogonal to
    // the content sub-type handled by `switchContentTestType`. Sets the coherent
    // `category` / `type` / `testTypes` shape for each mode, mirroring the
    // per-category init in `initialExperienceFeDto`. Variations and audience are
    // reshaped alongside this by the aiChat listener (they can't be — and
    // shouldn't be — reset from inside this slice).
    setExperienceMode: (
      draft,
      action: PayloadAction<{
        mode: "test" | "personalization";
        // This reducer only owns `draft.experience` — it can't reach into
        // the `redirects`/`onsiteEdits`/`onsiteInjections` slices to see
        // what content was actually built as a personalization, so the
        // caller (which has `getState()`) resolves presence there and hands
        // the answer in. Checked in priority order: theme > url > template >
        // onsite. `content/advanced` (every mod section shown) is the
        // fallback only when nothing was actually built yet.
        existingMods?: {
          hasThemeMods: boolean;
          hasUrlMods: boolean;
          hasTemplateMods: boolean;
          hasOnsiteMods: boolean;
          hasOffers: boolean;
        };
      }>
    ) => {
      if (!draft.isSuccess || !draft.experience) {
        return;
      }

      if (action.payload.mode === "personalization") {
        const mods = action.payload.existingMods;
        const isOffer =
          mods?.hasOffers || draft.experience.type === "personalization/offer";

        draft.experience.category = "personalization";
        // `offerTest` uses `personalization/offer` for both categories, so an
        // offer experience keeps that type rather than flattening to the plain
        // content-personalization one.
        draft.experience.type = isOffer
          ? "personalization/offer"
          : "personalization";
        // Keep the flag for each content kind that still HAS content. A
        // personalization has no `content/*` sub-type to carry this — these
        // flags are the only record — and `activatedModArray` is derived
        // entirely from them (see `evaluateTestTypes`). Blanket-clearing them
        // left a converted experience showing "No modifications added" with
        // its content still in state and correctly keyed, just with no section
        // marked active to render it. This mirrors the `existingMods`
        // inference the test direction already does.
        draft.experience.testTypes = {
          ...draft.experience.testTypes,
          hasTestContent: !!(
            mods?.hasThemeMods ||
            mods?.hasUrlMods ||
            mods?.hasTemplateMods ||
            mods?.hasOnsiteMods
          ),
          hasTestContentUrl: !!mods?.hasUrlMods,
          hasTestContentTemplate: !!mods?.hasTemplateMods,
          hasTestContentTheme: !!mods?.hasThemeMods,
          // "Advanced" means "show every content section", which is a
          // test-side fallback for an unbuilt experience. A personalization
          // shows exactly the sections it has content for.
          hasTestContentAdvanced: false,
          hasTestContentOnsite: !!mods?.hasOnsiteMods,
        };
      } else if (
        action.payload.existingMods?.hasOffers ||
        draft.experience.type === "personalization/offer"
      ) {
        // An offer experience is NOT a content experience, and the ladder below
        // has no branch for it — it would fall through to `content/advanced`,
        // and `ExperimentModificationsWrapper` matches `.includes("content")`
        // before it ever reaches the offer case, rendering the wrong body.
        //
        // The current type matters as much as `hasOffers`: the agent sets the
        // mode BEFORE building the offer, so at this point there is usually
        // nothing in the offers slice to infer from yet.
        //
        // `personalization/offer` is the type for BOTH categories (see
        // `offerTest`) — deliberately, not a copy-paste slip.
        draft.experience.category = "experiment";
        draft.experience.type = "personalization/offer";
        draft.experience.testTypes = {
          ...draft.experience.testTypes,
          hasTestCampaign: true,
          hasTestContent: false,
          hasTestContentUrl: false,
          hasTestContentTemplate: false,
          hasTestContentTheme: false,
          hasTestContentAdvanced: false,
          hasTestContentOnsite: false,
        };
      } else {
        const mods = action.payload.existingMods;
        const inferredType: ExperienceType = mods?.hasThemeMods
          ? "content/theme"
          : mods?.hasUrlMods
            ? "content/url"
            : mods?.hasTemplateMods
              ? "content/template"
              : mods?.hasOnsiteMods
                ? "content/onsiteEdits"
                : "content/advanced";

        draft.experience.category = "experiment";
        draft.experience.type = inferredType;
        draft.experience.testTypes = {
          ...draft.experience.testTypes,
          hasTestContent: true,
          hasTestContentUrl: inferredType === "content/url",
          hasTestContentTemplate: inferredType === "content/template",
          hasTestContentTheme: inferredType === "content/theme",
          hasTestContentAdvanced: inferredType === "content/advanced",
          hasTestContentOnsite: inferredType === "content/onsiteEdits",
        };
      }
    },
    resetExperienceAnalysisToStored: (draft) => {
      if (!draft.isSuccess || !draft.experience || !draft.storedExperience) {
        return;
      }

      draft.experience.experienceAnalysis.analyticsViewType =
        draft.storedExperience.experienceAnalysis.analyticsViewType;
    },
    setExperienceIntegration: (
      draft,
      action: PayloadAction<{
        name: IntegrationName;
        experienceSettings: StayAiExperienceSettingsType;
        enabled: boolean;
      }>
    ) => {
      if (!draft.isSuccess || !draft.experience) {
        return;
      }

      if (!draft.experience.experienceIntegrations) {
        draft.experience.experienceIntegrations = [];
      }

      if (action.payload.name === "StayAi") {
        const stayAi = (
          draft.experience.experienceIntegrations || []
        ).findIndex((ei) => ei.integration.name === "StayAi");

        if (stayAi > -1) {
          draft.experience.experienceIntegrations[stayAi].experienceSettings =
            action.payload.experienceSettings;
          draft.experience.experienceIntegrations[stayAi].enabled =
            action.payload.enabled;
        } else {
          draft.experience.experienceIntegrations.push({
            integration: {
              enabled: true,
              description: "Stay.ai",
              name: "StayAi",
              apis: [],
              globalSettings: {},
            },
            enabled: action.payload.enabled,
            experienceSettings: action.payload.experienceSettings,
          });
        }
      }
    },
    initExperienceAnalysis: (
      draft,
      action: PayloadAction<{
        experienceAnalysis: ExperienceAnalysisDto;
      }>
    ) => {
      if (!draft.isSuccess || !draft.experience) {
        return;
      }

      draft.experience.experienceAnalysis = action.payload.experienceAnalysis;
      if (draft.storedExperience) {
        draft.storedExperience.experienceAnalysis =
          action.payload.experienceAnalysis;
      }
    },
    setHasClickedSave: (draft, action: PayloadAction<boolean>) => {
      draft.hasClickedSave = action.payload;
    },
  },
  selectors: {
    selectedExperienceState: (state) => state,
    selectedExperience: (state) => state.experience as ExperienceFe,
    selectedExperienceCategory: (state) => state.experience?.category,
    selectedStoredExperience: (state) => state?.storedExperience,
    selectedExperienceStayAiIntegration: (state) => {
      return (
        (state.experience as ExperienceFe).experienceIntegrations || []
      ).find((ei) => ei.integration.name === "StayAi") as
        | ExperienceIntegrationDto<
            "StayAi",
            StayAiGlobalSettingsType,
            StayAiExperienceSettingsType
          >
        | undefined;
    },
    hasClickedSave: (state) => state.hasClickedSave,
    hasSubscriptionIntegration: (state) => {
      const integrations =
        (state.experience as ExperienceFe)?.experienceIntegrations || [];
      return integrations.some(
        (ei) =>
          (ei.integration.name === "StayAi" ||
            ei.integration.name === "Recharge") &&
          ei.enabled
      );
    },
  },
});

export const {
  resetExperience,
  initExperimentFromDto,
  initExperienceDetails,
  setExperienceId,
  recordCompletedSave,
  setShippingSaveId,
  setExperienceTabWithoutRedirect,
  setExperienceTab,
  setExperienceDetails,
  setExperienceViewMode,
  setExperienceActionMode,
  setExperienceCurrency,
  setExperienceShippingProfile,
  setExperienceShippingTestMethodDefinition,
  setExperienceAnalyticsViewType,
  setExperienceIntegration,
  deleteShippingExpMethodDefinitionById,
  toggleRequiresLink,
  setLinkBaseUrl,
  setExperienceShippingCarrierProvider,
  initPersonalizationFromDto,
  resetTestType,
  switchContentTestType,
  setExperienceMode,
  resetExperienceAnalysisToStored,
  initExperienceAnalysis,
  setHasClickedSave,
} = experienceSlice.actions;

export const {
  selectedExperienceState,
  selectedExperience,
  selectedExperienceCategory,
  selectedStoredExperience,
  selectedExperienceStayAiIntegration,
  hasClickedSave,
  hasSubscriptionIntegration,
} = experienceSlice.selectors;

export const experienceReducer = experienceSlice.reducer;
